import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem, 
  Badge, 
  Box,
  Chip
} from '@mui/material';
import { 
  faBell, 
  faUser, 
  faSignOutAlt, 
  faCog 
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const MembroTopoMobile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/inicio/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
    handleMenuClose();
  };

  const notifications = [
    { id: 1, message: 'Nova lição disponível', time: '2 min atrás', read: false },
    { id: 2, message: 'Sua declaração foi aprovada!', time: '1 hora atrás', read: false },
    { id: 3, message: 'Você subiu no ranking!', time: '3 horas atrás', read: true }
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        background: 'linear-gradient(135deg, #225477 0%, #4688ae 100%)',
        boxShadow: '0 2px 20px rgba(0,0,0,0.1)'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: '60px !important' }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="/assets/images/logotipo-horizontal.png" 
            alt="Logo" 
            style={{ 
              height: '32px', 
              filter: 'brightness(0) invert(1)',
              marginRight: '12px'
            }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'white', 
              fontWeight: 600,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Bnei Noach
          </Typography>
        </Box>

        {/* User Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Notifications */}
          <IconButton
            onClick={handleNotificationMenuOpen}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
          >
            <Badge badgeContent={unreadCount} color="error" max={9}>
              <FontAwesomeIcon icon={faBell} style={{ fontSize: '1rem' }} />
            </Badge>
          </IconButton>

          {/* User Profile */}
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              p: 0,
              border: '2px solid rgba(255,255,255,0.3)',
              '&:hover': {
                border: '2px solid rgba(255,255,255,0.5)'
              }
            }}
          >
            <Avatar 
              src={user?.perfil || '/assets/images/perfil.png'} 
              alt="Perfil"
              sx={{ width: 36, height: 36 }}
            />
          </IconButton>
        </Box>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { navigate('/membro'); handleMenuClose(); }}>
          <FontAwesomeIcon icon={faUser} style={{ marginRight: 12, color: '#225477' }} />
          Meu Perfil
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <FontAwesomeIcon icon={faCog} style={{ marginRight: 12, color: '#225477' }} />
          Configurações
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ color: '#d32f2f' }}>
          <FontAwesomeIcon icon={faSignOutAlt} style={{ marginRight: 12 }} />
          Sair
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 300,
            maxHeight: 400,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ color: '#225477', fontWeight: 600 }}>
            Notificações
          </Typography>
        </Box>
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <MenuItem 
              key={notification.id} 
              sx={{ 
                p: 2, 
                backgroundColor: notification.read ? 'transparent' : '#f3f8ff',
                borderBottom: '1px solid #f0f0f0'
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ fontWeight: notification.read ? 400 : 600 }}>
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {notification.time}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Nenhuma notificação
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </AppBar>
  );
};

export default MembroTopoMobile; 